import type { SupportStatus } from '../types';

type MessageDirection = { from: 'client' | 'agent' | 'system' };

/**
 * The status to *display*, framed as "whose turn is it?" from the client's
 * point of view.
 *
 * The raw Zendesk status is an unreliable signal of conversational turn: an
 * agent can reply and leave a ticket `open` (so it never flips to `pending`),
 * and Zendesk also transitions status asynchronously, so the chip would
 * otherwise sit on a stale value. Message direction is the immediate, reliable
 * signal:
 *   - last message from the **agent**  → the client's turn → `pending`
 *     ("Awaiting Your Reply")
 *   - last message from the **client** → support's turn    → `open`
 *     ("In Progress")
 *
 * Terminal states win: `closed` always shows Closed, and `solved` shows
 * Resolved unless the client has replied since (which reopens the ticket).
 */
export function deriveDisplayStatus(
  status: SupportStatus,
  messages: ReadonlyArray<MessageDirection>
): SupportStatus {
  if (status === 'closed') {
    return 'closed';
  }

  const lastFrom = messages[messages.length - 1]?.from;

  if (status === 'solved') {
    // A client reply to a solved ticket reopens it.
    return lastFrom === 'client' ? 'open' : 'solved';
  }

  // Active ticket (new / open / pending): derive the turn from the last message.
  if (lastFrom === 'agent') {
    return 'pending';
  }
  if (lastFrom === 'client') {
    return 'open';
  }
  return status;
}

export default deriveDisplayStatus;
